import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { IcChartBar, IcPdf, IcExcel } from "../../Icons";
import Header from "../../layout/Header/Header";
import { Button, ButtonGroup, Table, Input, Label } from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { pdfHeader, pdfFooter, pdfTableStyles } from "../../../pdfUtils";
import axios from "axios";
import moment from "moment";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  ResponsiveContainer, ComposedChart, BarChart,
  Area, Bar, Line, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ReferenceLine,
} from "recharts";

// ─────────────────────────────────────────────────────────────────────────────
// Paleta del panel de gráficas.
// Un color por concepto, el mismo en las cuatro gráficas. Validada para
// daltonismo (deutan/protan/tritan) sobre fondo blanco.
//   azul    = utilidad y margen — lo que te queda
//   naranja = gastos
//   verde   = cobranza
//   gris    = venta, va de referencia y no compite con las demás
// El retiro usa azul/rojo porque ahí lo que importa es el signo.
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  utilidad: "#2a78d6",
  gastos:   "#eb6834",
  cobranza: "#1baf7a",
  venta:    "#cbd0d6",
  negativo: "#e34948",
  grid:     "#e8e9ec",
  tinta:    "#0b0b0b",
  suave:    "#6b7280",
};

const MES_CORTO = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

// Formato corto para los ejes
const ejeMXN = (v) => {
  const n = Math.abs(Number(v) || 0);
  if (n >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (n >= 1000)    return `$${Math.round(v / 1000)}k`;
  return `$${Math.round(v || 0)}`;
};

const ejeStyle  = { fontSize: 11, fill: C.suave, fontVariantNumeric: "tabular-nums" };
const gridProps = { stroke: C.grid, strokeWidth: 1, vertical: false };

// Tarjeta del tooltip — la misma en las cuatro gráficas
function TipCard({ titulo, filas }) {
  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
      boxShadow: "0 8px 24px rgba(11,11,11,0.10)", padding: "10px 12px", minWidth: 180,
    }}>
      <div style={{ fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em",
        textTransform: "uppercase", color: C.suave, marginBottom: 6 }}>{titulo}</div>
      {filas.map((f) => (
        <div key={f.label} style={{ display: "flex", alignItems: "center", gap: 10,
          justifyContent: "space-between", fontSize: "0.78rem", lineHeight: 1.75 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6, color: "#374151" }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: f.color,
              display: "inline-block", flexShrink: 0 }} />
            {f.label}
          </span>
          <b style={{ color: C.tinta, fontVariantNumeric: "tabular-nums" }}>{f.valor}</b>
        </div>
      ))}
    </div>
  );
}

// Contenedor de cada gráfica
function Tarjeta({ titulo, sub, alto, children }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #eceef1", borderRadius: 14,
      padding: "16px 18px 12px", height: "100%" }}>
      <div style={{ fontSize: "0.92rem", fontWeight: 700, color: C.tinta }}>{titulo}</div>
      <div style={{ fontSize: "0.74rem", color: C.suave, marginBottom: 14 }}>{sub}</div>
      <div style={{ height: alto }}>{children}</div>
    </div>
  );
}

// Cifra grande + mini gráfica de los 12 meses
function StatTile({ etiqueta, valor, nota, color, serie }) {
  const max = Math.max(...serie.map((v) => Math.abs(v)), 1);
  const paso = serie.length > 1 ? 100 / (serie.length - 1) : 100;
  const d = serie.map((v, i) => `${i ? "L" : "M"}${(i * paso).toFixed(1)},${(26 - (v / max) * 22).toFixed(1)}`).join(" ");
  return (
    <div style={{ background: "#fff", border: "1px solid #eceef1", borderRadius: 14,
      padding: "14px 16px", height: "100%" }}>
      <div style={{ fontSize: "0.64rem", fontWeight: 700, letterSpacing: "0.1em",
        textTransform: "uppercase", color: C.suave }}>{etiqueta}</div>
      <div style={{ fontSize: "1.45rem", fontWeight: 700, color, margin: "4px 0 2px",
        letterSpacing: "-0.02em" }}>{valor}</div>
      <div style={{ fontSize: "0.7rem", color: C.suave }}>{nota}</div>
      <svg viewBox="0 0 100 30" preserveAspectRatio="none"
        style={{ width: "100%", height: 30, marginTop: 8, display: "block" }} aria-hidden="true">
        <path d={d} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke"
          strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
      </svg>
    </div>
  );
}

function BalanceMensual() {
  const { user } = useContext(AuthContext);
  const URL_GRAFICAS = process.env.REACT_APP_URL_GRAFICAS;

  const anioInicio = 2021;
  const anioActual = new Date().getFullYear();

  const anios = Array.from({ length: anioActual - anioInicio + 1 }, (_, i) =>
    String(anioInicio + i)
  );

  // Estados
  const [selectedYear, setSelectedYear] = useState(String(anioActual));
  const [selectedFechaInicio, setSelectedFechaInicio] = useState(
    `${anioActual}-01-01`
  );
  const [selectedFechaFin, setSelectedFechaFin] = useState(
    moment().format("YYYY-MM-DD") // año a la fecha
  );

  const [balance, setBalance] = useState(null);
  const [incluirMesActual, setIncluirMesActual] = useState(true);
  const [vista, setVista] = useState("tabla"); // "tabla" | "grafica"

  // Cuántas columnas de mes mostrar: año actual → hasta mes en curso; años pasados → 12
  const visibleMonthCount = selectedYear === String(anioActual)
    ? new Date().getMonth() + 1
    : 12;

  useEffect(() => {
    if (!selectedFechaInicio || !selectedFechaFin) return;

    const params = incluirMesActual ? '' : '?incluirMesActual=false';

    axios
      .get(
        `${URL_GRAFICAS}/balanceMensual/${selectedFechaInicio}/${selectedFechaFin}${params}`,
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then((response) => {
        setBalance(response.data);
      })
      .catch(() => {});
  }, [selectedFechaInicio, selectedFechaFin, incluirMesActual]);

  const onYearChange = (year) => {
    setSelectedYear(year);
    setSelectedFechaInicio(`${year}-01-01`);
    // Para el año en curso, sólo pedir hasta hoy — evita ceros en meses futuros
    setSelectedFechaFin(
      year === String(anioActual)
        ? moment().format("YYYY-MM-DD")
        : `${year}-12-31`
    );
  };

  function PDFBalanceMensual() {
    if (!balance) return;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const startY = pdfHeader(doc, "Balance Mensual", selectedYear);

    // ── Sólo meses visibles — igual que la vista ──────────────────────────────
    const MONTH_SHORT = {
      Enero: "Ene", Febrero: "Feb", Marzo: "Mar", Abril: "Abr",
      Mayo: "May", Junio: "Jun", Julio: "Jul", Agosto: "Ago",
      Septiembre: "Sep", Octubre: "Oct", Noviembre: "Nov", Diciembre: "Dic",
    };

    // balance.columns = [mes0..mes11, "Total", "Promedio"]
    // Filtramos: sólo meses hasta visibleMonthCount + Total + Promedio
    const visibleCols = balance.columns.filter((c, idx) =>
      idx >= 12 || idx < visibleMonthCount
    );
    const nMonths     = visibleCols.filter((c) => c !== "Total" && c !== "Promedio").length;
    const totalIdx    = nMonths + 1;   // col 0 = Concepto → col nMonths+1 = Total
    const promedioIdx = nMonths + 2;   //                  → col nMonths+2 = Promedio

    const headCols = visibleCols.map((c) => MONTH_SHORT[c] ?? c);
    const head     = [["Concepto", ...headCols]];

    const body = balance.rows.map((r) => {
      const isPct = r.label.includes("%");
      const months = r.months
        .slice(0, visibleMonthCount)
        .map((v) => isPct ? `${Math.round((Number(v) || 0) * 100)}%` : formatMXN(v));
      const total    = isPct ? `${Math.round((Number(r.total)    || 0) * 100)}%` : formatMXN(r.total);
      const promedio = isPct ? `${Math.round((Number(r.promedio) || 0) * 100)}%` : formatMXN(r.promedio);
      return [r.label, ...months, total, promedio];
    });

    // ── Anchos dinámicos ──────────────────────────────────────────────────────
    const pageW     = doc.internal.pageSize.getWidth();
    const marginL   = 8, marginR = 8;
    const available = pageW - marginL - marginR;
    const conceptoW = 26;
    const summaryW  = 21;
    const monthW    = (available - conceptoW - summaryW * 2) / Math.max(nMonths, 1);

    const columnStyles = { 0: { cellWidth: conceptoW, halign: "left" } };
    visibleCols.forEach((c, i) => {
      columnStyles[i + 1] = {
        cellWidth: (c === "Total" || c === "Promedio") ? summaryW : monthW,
        halign: "center",
      };
    });

    // ── Tabla ─────────────────────────────────────────────────────────────────
    doc.autoTable({
      startY,
      head,
      body,
      columnStyles,

      styles: {
        fontSize:    7.5,
        cellPadding: { top: 2.5, right: 3, bottom: 2.5, left: 3 },
        overflow:    "hidden",
        font:        "helvetica",
        lineColor:   [226, 232, 240],
        lineWidth:   0.25,
      },
      headStyles: {
        fillColor:   [15, 23, 42],   // azul oscuro — para TODOS los encabezados
        textColor:   [255, 255, 255],
        fontStyle:   "bold",
        fontSize:    7,
        halign:      "center",
        cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
      },
      bodyStyles: { fontSize: 7.5 },
      alternateRowStyles: { fillColor: [248, 250, 252] },

      didParseCell: function (data) {
        const label    = data.row.raw?.[0];
        const colIndex = data.column.index;
        const isHead   = data.row.section === "head";

        // Negritas sólo en filas de cuerpo
        if (!isHead && ["Venta", "Gastos", "Utilidad", "Cobranza", "Retiro"].includes(label)) {
          data.cell.styles.fontStyle = "bold";
        }

        // Columna TOTAL: gris en header y en celdas (texto oscuro para contrastar)
        if (colIndex === totalIdx) {
          data.cell.styles.fillColor = [242, 242, 242];
          data.cell.styles.textColor = [15, 23, 42];
        }

        // Columna PROMEDIO: gris SÓLO en celdas — header igual que los meses (azul oscuro)
        if (colIndex === promedioIdx && !isHead) {
          data.cell.styles.fillColor = [242, 242, 242];
          data.cell.styles.textColor = [15, 23, 42];
        }
      },

      margin:     { left: marginL, right: marginR },
      tableWidth: available,
    });

    pdfFooter(doc);
    doc.save(`Balance_Mensual_${selectedYear}.pdf`);
  }

  async function excelBalance() {
    if (!balance?.columns?.length || !balance?.rows?.length) return;

    const headers = [
      "CONCEPTO",
      ...balance.columns.map((c) => c.toUpperCase()),
    ]; // incluye Total/Promedio
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("BalanceMensual");

    // ===== helpers =====
    const colLetter = (n) => {
      let s = "";
      while (n > 0) {
        const m = (n - 1) % 26;
        s = String.fromCharCode(65 + m) + s;
        n = Math.floor((n - 1) / 26);
      }
      return s;
    };

    const grayFill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF2F2F2" },
    };

    const moneyFmt = '"$"#,##0;[Red]"$"#,##0';
    const pctFmt = "0.00%";

    // columnas: A = Concepto, B..M = meses (12), N=Total, O=Promedio
    const firstMonthCol = 2; // B
    const lastMonthCol = 13; // M (12 meses)
    const totalCol = lastMonthCol + 1; // N
    const avgCol = lastMonthCol + 2; // O

    // ===== header =====
    ws.addRow(headers);
    ws.getRow(1).font = { bold: true, size: 12 };
    ws.getRow(1).alignment = { vertical: "middle", horizontal: "center" };
    ws.getRow(1).height = 18;

    // gris en Total/Promedio header
    ws.getCell(1, totalCol).fill = grayFill;
    ws.getCell(1, avgCol).fill = grayFill;

    // anchos
    ws.getColumn(1).width = 16;
    for (let c = 2; c <= avgCol; c++) ws.getColumn(c).width = 14;

    // ===== obtener filas base desde balance =====
    const rowByLabel = {};
    balance.rows.forEach((r) => (rowByLabel[r.label] = r));

    const venta = rowByLabel["Venta"]?.months || Array(12).fill(0);
    const gastos = rowByLabel["Gastos"]?.months || Array(12).fill(0);
    const cobranza = rowByLabel["Cobranza"]?.months || Array(12).fill(0);

    // ===== filas (en el mismo orden que tu tabla) =====
    const labels = [
      "Venta",
      "Gastos",
      "Gastos %",
      "Utilidad",
      "Utilidad %",
      "Cobranza",
      "Retiro",
    ];

    const rowIndex = {}; // label -> row number en excel

    labels.forEach((label) => {
      const row = [label];

      if (label === "Venta") row.push(...venta);
      else if (label === "Gastos") row.push(...gastos);
      else if (label === "Cobranza") row.push(...cobranza);
      else row.push(...Array(12).fill(null)); // se llenará con fórmulas

      // placeholders Total y Promedio (se pondrán con fórmula abajo)
      row.push(null, null);

      const r = ws.addRow(row);
      rowIndex[label] = r.number;
    });

    // ===== aplicar fórmulas =====
    const rVenta = rowIndex["Venta"];
    const rGastos = rowIndex["Gastos"];
    const rGastosPct = rowIndex["Gastos %"];
    const rUtil = rowIndex["Utilidad"];
    const rUtilPct = rowIndex["Utilidad %"];
    const rCob = rowIndex["Cobranza"];
    const rRet = rowIndex["Retiro"];

    // fórmulas por mes (B..M)
    for (let c = firstMonthCol; c <= lastMonthCol; c++) {
      const L = colLetter(c);
      ws.getCell(rGastosPct, c).value = {
        formula: `${L}${rGastos}/${L}${rVenta}`,
      };
      ws.getCell(rUtil, c).value = { formula: `${L}${rVenta}-${L}${rGastos}` };
      ws.getCell(rUtilPct, c).value = { formula: `${L}${rUtil}/${L}${rVenta}` };
      ws.getCell(rRet, c).value = { formula: `${L}${rCob}-${L}${rGastos}` };
    }

    // Total y Promedio por fila
    labels.forEach((label) => {
      const r = rowIndex[label];
      const start = `${colLetter(firstMonthCol)}${r}`; // B#
      const end = `${colLetter(lastMonthCol)}${r}`; // M#

      ws.getCell(r, totalCol).value = { formula: `SUM(${start}:${end})` };
      ws.getCell(r, avgCol).value = { formula: `AVERAGE(${start}:${end})` };
    });

    // ===== formatos =====
    const moneyRows = ["Venta", "Gastos", "Utilidad", "Cobranza", "Retiro"];
    const pctRows = ["Gastos %", "Utilidad %"];

    // Estilos por fila
    labels.forEach((label) => {
      const r = rowIndex[label];
      ws.getRow(r).alignment = { vertical: "middle", horizontal: "center" };
      ws.getCell(r, 1).alignment = { vertical: "middle", horizontal: "left" };

      const isBold = [
        "Venta",
        "Gastos",
        "Utilidad",
        "Cobranza",
        "Retiro",
      ].includes(label);
      if (isBold) ws.getRow(r).font = { bold: true };

      // Formato numérico
      for (let c = firstMonthCol; c <= avgCol; c++) {
        const cell = ws.getCell(r, c);

        // gris Total/Promedio
        if (c === totalCol || c === avgCol) cell.fill = grayFill;

        if (moneyRows.includes(label)) cell.numFmt = moneyFmt;
        if (pctRows.includes(label)) cell.numFmt = pctFmt;

        // bordes tipo tabla
        cell.border = {
          top: { style: "thin", color: { argb: "FFD0D0D0" } },
          left: { style: "thin", color: { argb: "FFD0D0D0" } },
          bottom: { style: "thin", color: { argb: "FFD0D0D0" } },
          right: { style: "thin", color: { argb: "FFD0D0D0" } },
        };
      }

      // borde para la celda Concepto también
      ws.getCell(r, 1).border = {
        top: { style: "thin", color: { argb: "FFD0D0D0" } },
        left: { style: "thin", color: { argb: "FFD0D0D0" } },
        bottom: { style: "thin", color: { argb: "FFD0D0D0" } },
        right: { style: "thin", color: { argb: "FFD0D0D0" } },
      };
    });

    // congelar encabezado y primera columna
    ws.views = [{ state: "frozen", xSplit: 1, ySplit: 1 }];

    // ===== descargar =====
    const buf = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `BalanceMensual_${selectedYear}.xlsx`
    );
  }

  const bgGray = "#f2f2f2";

  const formatMXN = (value) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);

 const formatPct = (value) => {
  const n = Number(value) || 0;
  return `${(n * 100).toFixed(2)}%`;
};

  // ── Datos para las gráficas ────────────────────────────────────────────────
  // Solo los meses visibles, para que la gráfica diga lo mismo que la tabla.
  const grafica = useMemo(() => {
    if (!balance?.rows?.length) return null;
    const porLabel = {};
    balance.rows.forEach((r) => (porLabel[r.label] = r));
    const serie = (l) => porLabel[l]?.months || Array(12).fill(0);

    const venta    = serie("Venta");
    const gastos   = serie("Gastos");
    const utilidad = serie("Utilidad");
    const cobranza = serie("Cobranza");
    const retiro   = serie("Retiro");

    const datos = [];
    for (let i = 0; i < visibleMonthCount; i++) {
      datos.push({
        mes: MES_CORTO[i],
        venta: venta[i], gastos: gastos[i], utilidad: utilidad[i],
        cobranza: cobranza[i], retiro: retiro[i],
        // Diferencia entre lo vendido y lo cobrado, para el tooltip
        porCobrar: (venta[i] || 0) - (cobranza[i] || 0),
        margen: venta[i] ? (utilidad[i] / venta[i]) * 100 : 0,
      });
    }

    const suma = (a) => a.slice(0, visibleMonthCount).reduce((x, y) => x + (Number(y) || 0), 0);
    const tVenta = suma(venta), tUtil = suma(utilidad), tGasto = suma(gastos);
    const n = Math.max(visibleMonthCount, 1);

    // Índices a etiquetar directamente: el mejor mes de utilidad y el peor de gasto
    const idxMax = (arr) => {
      let mejor = 0;
      for (let i = 1; i < visibleMonthCount; i++) if ((arr[i] || 0) > (arr[mejor] || 0)) mejor = i;
      return mejor;
    };

    return {
      datos,
      totales: {
        venta: tVenta,
        utilidad: tUtil,
        gastos: tGasto,
        cobranza: suma(cobranza),
        retiro: suma(retiro),
        margen: tVenta ? (tUtil / tVenta) * 100 : 0,
      },
      series: { venta, gastos, utilidad, cobranza, retiro },
      utilProm: tUtil / n,
      gastoProm: tGasto / n,
      mejorMes: idxMax(utilidad),
      peorGasto: idxMax(gastos),
      hayNegativo: retiro.slice(0, visibleMonthCount).some((v) => v < 0),
    };
  }, [balance, visibleMonthCount]);

  return (
    <>
      <Header />
      {user.menu_usuarios ? (
        <div className="card container col-sm-12">

          {/* ── Page header ─────────────────────────────────────────────── */}
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <IcChartBar style={{ marginRight: 8, color: "#8b5cf6" }} />
                Balance Mensual
              </h3>
              <p className="page-subtitle">Análisis mensual de venta, gastos y utilidad</p>
            </div>
            <div className="page-actions">
            <ButtonGroup>
              <Button size="sm" color="danger" onClick={PDFBalanceMensual}>
                <IcPdf style={{ marginRight: 4 }} /> PDF
              </Button>
              <Button size="sm" color="primary" onClick={excelBalance}>
                <IcExcel style={{ marginRight: 4 }} /> Excel
              </Button>
              </ButtonGroup>
            </div>
          </div>

          {/* ── Filters ─────────────────────────────────────────────────── */}
          <div className="filter-bar">
            <div>
              <Label>Año</Label>
              <Input
                bsSize="sm"
                type="select"
                style={{ minWidth: 100 }}
                value={selectedYear}
                onChange={(e) => onYearChange(e.target.value)}
              >
                {anios.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Input>
            </div>
            {selectedYear === String(anioActual) && (
              <div>
                <Label>Mes en curso</Label>
                <Input
                  bsSize="sm"
                  type="select"
                  style={{ minWidth: 120 }}
                  value={incluirMesActual ? "si" : "no"}
                  onChange={(e) => setIncluirMesActual(e.target.value === "si")}
                >
                  <option value="si">Sí</option>
                  <option value="no">No</option>
                </Input>
              </div>
            )}
            <div>
              <Label>Vista</Label>
              <ButtonGroup size="sm" style={{ display: "block" }}>
                <Button color="primary" outline={vista !== "tabla"}
                  onClick={() => setVista("tabla")}>Tabla</Button>
                <Button color="primary" outline={vista !== "grafica"}
                  onClick={() => setVista("grafica")}>Gráfica</Button>
              </ButtonGroup>
            </div>
          </div>

          {/* ── Panel de gráficas ───────────────────────────────────────── */}
          {vista === "grafica" && grafica && (
            <div style={{ paddingBottom: 18 }}>

              {/* Cifras del año */}
              <div className="row" style={{ margin: "0 -6px 14px" }}>
                {[
                  { etiqueta: "Venta", valor: formatMXN(grafica.totales.venta),
                    nota: `${visibleMonthCount} meses · ${formatMXN(grafica.totales.venta / visibleMonthCount)} al mes`,
                    color: C.tinta, serie: grafica.series.venta },
                  { etiqueta: "Utilidad", valor: formatMXN(grafica.totales.utilidad),
                    nota: `Margen ${grafica.totales.margen.toFixed(1)}%`,
                    color: C.utilidad, serie: grafica.series.utilidad },
                  { etiqueta: "Gastos", valor: formatMXN(grafica.totales.gastos),
                    nota: `${(100 - grafica.totales.margen).toFixed(1)}% de la venta`,
                    color: C.gastos, serie: grafica.series.gastos },
                  { etiqueta: "Cobranza", valor: formatMXN(grafica.totales.cobranza),
                    nota: `Retiro ${formatMXN(grafica.totales.retiro)}`,
                    color: C.cobranza, serie: grafica.series.cobranza },
                ].map((t) => (
                  <div key={t.etiqueta} className="col-6 col-xl-3 mb-2" style={{ padding: "0 6px" }}>
                    <StatTile {...t} />
                  </div>
                ))}
              </div>

              <div className="row" style={{ margin: "0 -6px" }}>

                {/* 1. Vendido contra cobrado — la gráfica principal */}
                <div className="col-12 col-xl-7 mb-3" style={{ padding: "0 6px" }}>
                  <Tarjeta
                    titulo="Lo que vendiste contra lo que cobraste"
                    sub="La barra gris es la venta del mes; la verde, el dinero que de verdad entró"
                    alto={280}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={grafica.datos} margin={{ top: 8, right: 6, left: -8, bottom: 0 }} barGap={2}>
                        <defs>
                          <linearGradient id="gCobranza" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={C.cobranza} stopOpacity="1" />
                            <stop offset="100%" stopColor={C.cobranza} stopOpacity="0.74" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid {...gridProps} />
                        <XAxis dataKey="mes" tick={ejeStyle} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={ejeMXN} tick={ejeStyle} axisLine={false} tickLine={false} width={58} />
                        <Tooltip cursor={{ fill: "rgba(11,11,11,0.04)" }} content={({ active, payload, label }) =>
                          active && payload?.length ? (
                            <TipCard titulo={label} filas={[
                              { label: "Venta", color: C.venta, valor: formatMXN(payload[0].payload.venta) },
                              { label: "Cobranza", color: C.cobranza, valor: formatMXN(payload[0].payload.cobranza) },
                              { label: payload[0].payload.porCobrar >= 0 ? "Quedó por cobrar" : "Cobraste de atrasados",
                                color: payload[0].payload.porCobrar >= 0 ? C.gastos : C.cobranza,
                                valor: formatMXN(Math.abs(payload[0].payload.porCobrar)) },
                            ]} />
                          ) : null} />
                        {/* El texto de la leyenda va en tinta, no en el color de la
                            serie: el color lo carga el punto de al lado. */}
                        <Legend verticalAlign="bottom" height={28} iconType="circle" iconSize={8}
                          wrapperStyle={{ fontSize: 12 }}
                          formatter={(v) => <span style={{ color: C.suave }}>{v}</span>} />
                        <Bar dataKey="venta" name="Venta" fill={C.venta} radius={[4, 4, 0, 0]}
                          isAnimationActive={false} />
                        <Bar dataKey="cobranza" name="Cobranza" fill="url(#gCobranza)" radius={[4, 4, 0, 0]}
                          isAnimationActive={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Tarjeta>
                </div>

                {/* 2. Utilidad mensual, en pesos */}
                <div className="col-12 col-xl-5 mb-3" style={{ padding: "0 6px" }}>
                  <Tarjeta
                    titulo="Utilidad mes a mes"
                    sub={`Venta menos gastos · promedio ${formatMXN(grafica.utilProm)} al mes`}
                    alto={280}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={grafica.datos} margin={{ top: 26, right: 14, left: 2, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gUtil" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={C.utilidad} stopOpacity="0.26" />
                            <stop offset="100%" stopColor={C.utilidad} stopOpacity="0.01" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid {...gridProps} />
                        <XAxis dataKey="mes" tick={ejeStyle} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={ejeMXN} tick={ejeStyle} axisLine={false} tickLine={false} width={58} />
                        <Tooltip cursor={{ stroke: C.suave, strokeWidth: 1 }} content={({ active, payload, label }) =>
                          active && payload?.length ? (
                            <TipCard titulo={label} filas={[
                              { label: "Venta", color: C.venta, valor: formatMXN(payload[0].payload.venta) },
                              { label: "Gastos", color: C.gastos, valor: formatMXN(payload[0].payload.gastos) },
                              { label: "Utilidad", color: C.utilidad, valor: formatMXN(payload[0].payload.utilidad) },
                            ]} />
                          ) : null} />
                        <ReferenceLine y={grafica.utilProm} stroke={C.suave} strokeWidth={1}
                          label={{ value: "promedio", position: "insideTopLeft", fill: C.suave, fontSize: 10 }} />
                        <Area type="monotone" dataKey="utilidad" stroke="none" fill="url(#gUtil)"
                          isAnimationActive={false} />
                        <Line type="monotone" dataKey="utilidad" name="Utilidad" stroke={C.utilidad}
                          strokeWidth={2} dot={{ r: 3, fill: C.utilidad, strokeWidth: 0 }}
                          activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2 }}
                          isAnimationActive={false}
                          label={({ x, y, index, value }) =>
                            index === grafica.mejorMes ? (
                              <text x={x} y={y - 12} fill={C.utilidad} fontSize={12} fontWeight={700}
                                textAnchor="middle">{ejeMXN(value)}</text>
                            ) : null} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Tarjeta>
                </div>

                {/* 3. Gastos, en su propia escala */}
                <div className="col-12 col-xl-7 mb-3" style={{ padding: "0 6px" }}>
                  <Tarjeta
                    titulo="Gastos mes a mes"
                    sub={`Escala propia — son chicos al lado de la venta · promedio ${formatMXN(grafica.gastoProm)} al mes`}
                    alto={260}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={grafica.datos} margin={{ top: 22, right: 6, left: -8, bottom: 0 }}>
                        <CartesianGrid {...gridProps} />
                        <XAxis dataKey="mes" tick={ejeStyle} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={ejeMXN} tick={ejeStyle} axisLine={false} tickLine={false} width={58} />
                        <Tooltip cursor={{ fill: "rgba(11,11,11,0.04)" }} content={({ active, payload, label }) =>
                          active && payload?.length ? (
                            <TipCard titulo={label} filas={[
                              { label: "Gastos", color: C.gastos, valor: formatMXN(payload[0].payload.gastos) },
                              { label: "Venta", color: C.venta, valor: formatMXN(payload[0].payload.venta) },
                              { label: "Peso sobre la venta", color: C.gastos,
                                valor: `${(100 - payload[0].payload.margen).toFixed(1)}%` },
                            ]} />
                          ) : null} />
                        <ReferenceLine y={grafica.gastoProm} stroke={C.suave} strokeWidth={1}
                          label={{ value: "promedio", position: "insideTopLeft", fill: C.suave, fontSize: 10 }} />
                        <Bar dataKey="gastos" name="Gastos" fill={C.gastos} radius={[4, 4, 0, 0]}
                          isAnimationActive={false}
                          label={({ x, y, width, index, value }) =>
                            index === grafica.peorGasto ? (
                              <text x={x + width / 2} y={y - 7} fill={C.gastos} fontSize={12} fontWeight={700}
                                textAnchor="middle">{ejeMXN(value)}</text>
                            ) : null} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Tarjeta>
                </div>

                {/* 4. Retiro */}
                <div className="col-12 col-xl-5 mb-3" style={{ padding: "0 6px" }}>
                  <Tarjeta
                    titulo="Retiro mes a mes"
                    sub={grafica.hayNegativo
                      ? "Cobranza menos gastos · en rojo los meses que cerraron en negativo"
                      : "Cobranza menos gastos · todos los meses cerraron en positivo"}
                    alto={260}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={grafica.datos} margin={{ top: 8, right: 6, left: -8, bottom: 0 }}>
                        <CartesianGrid {...gridProps} />
                        <XAxis dataKey="mes" tick={ejeStyle} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={ejeMXN} tick={ejeStyle} axisLine={false} tickLine={false} width={58} />
                        <Tooltip cursor={{ fill: "rgba(11,11,11,0.04)" }} content={({ active, payload, label }) =>
                          active && payload?.length ? (
                            <TipCard titulo={label} filas={[
                              { label: "Cobranza", color: C.cobranza, valor: formatMXN(payload[0].payload.cobranza) },
                              { label: "Gastos", color: C.gastos, valor: formatMXN(payload[0].payload.gastos) },
                              { label: "Retiro", color: payload[0].payload.retiro < 0 ? C.negativo : C.utilidad,
                                valor: formatMXN(payload[0].payload.retiro) },
                            ]} />
                          ) : null} />
                        <ReferenceLine y={0} stroke={C.suave} strokeWidth={1} />
                        <Bar dataKey="retiro" name="Retiro" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                          {grafica.datos.map((d, i) => (
                            <Cell key={i} fill={d.retiro < 0 ? C.negativo : C.utilidad} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Tarjeta>
                </div>
              </div>

              <p style={{ fontSize: "0.72rem", color: C.suave, margin: "2px 2px 0" }}>
                Pasa el cursor por cualquier mes para ver el detalle. Los montos exactos
                de los {visibleMonthCount} meses están en la vista de Tabla, y el PDF y el
                Excel siguen saliendo con la tabla completa.
              </p>
            </div>
          )}

          {vista === "tabla" && (
          <Table
            bordered
            size="sm"
            responsive={false}
            className="balance-table table-dark-header"
          >
            <thead>
              <tr>
                <th>Concepto</th>

                {balance?.columns?.map((c, idx) => {
                  // idx 0-11 = meses, 12 = Total, 13 = Promedio
                  if (idx < 12 && idx >= visibleMonthCount) return null;

                  return (
                    <th key={c} style={{ textAlign: "center" }}>
                      {c}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {balance?.rows?.map((r) => {
                const isPct = r.label.includes("%");
                const isBold = [
                  "Venta",
                  "Gastos",
                  "Utilidad",
                  "Cobranza",
                  "Retiro",
                ].includes(r.label);

                return (
                  <tr key={r.label}>
                    {/* Concepto */}
                    <td style={{ fontWeight: isBold ? "bold" : "normal" }}>
                      {r.label}
                    </td>

                    {/* Meses */}
                    {r.months.map((v, idx) => {
                      if (idx >= visibleMonthCount) return null;
                      return (
                        <td
                          key={idx}
                          style={{
                            textAlign: "center",
                            fontWeight: isBold ? "bold" : "normal",
                          }}
                        >
                          {isPct ? formatPct(v) : formatMXN(v)}
                        </td>
                      );
                    })}

                    {/* Total */}
                    <td
                      style={{
                        textAlign: "center",
                        fontWeight: isBold ? "bold" : "normal",
                        backgroundColor: bgGray,
                      }}
                    >
                      {isPct ? formatPct(r.total) : formatMXN(r.total)}
                    </td>

                    {/* Promedio */}
                    <td
                      style={{
                        textAlign: "center",
                        fontWeight: isBold ? "bold" : "normal",
                        backgroundColor: bgGray,
                      }}
                    >
                      {isPct ? formatPct(r.promedio) : formatMXN(r.promedio)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          )}
        </div>
      ) : undefined}
    </>
  );
}

export default BalanceMensual;
